import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { FilePlus, GitCompare, CheckCircle, FileText, Layers } from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import ApprobationsCrudShell from './ApprobationsCrudShell';
import {
  ENTITY_TYPES, WORKFLOW_STATUS, statusBadge, entityLabel, defaultStats, hasMeaningfulChainJson,
  mapRecordJsonFields, chainJsonFromTemplate, parseApiRows,
} from './approbationsShared';

const NS = 'creerWorkflowPage';
const API = '/api/approbations/workflows';
const TEMPLATES_API = '/api/approbations/templates';

const EMPTY = {
  workflow_code: '', name: '', description: '', entity_type: '', status: 'brouillon',
  priority: '0', chain_json: '[]', template_id: '', notes: '',
};

export default function CreerWorkflowPage() {
  const [templates, setTemplates] = useState([]);

  const loadTemplates = useCallback(async () => {
    try {
      const response = await localApiAdapter.get(TEMPLATES_API, { status: 'actif' });
      setTemplates(parseApiRows(response));
    } catch {
      setTemplates([]);
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const entityOpts = useMemo(() => ENTITY_TYPES.map((e) => ({ value: e.value, labelKey: e.labelKey })), []);
  const statusOpts = useMemo(() => WORKFLOW_STATUS.map((s) => ({ value: s.value, labelKey: s.labelKey })), []);

  const formSections = useMemo(() => ([
    {
      id: 'definition',
      titleKey: 'section_definition',
      fields: [
        { key: 'name', labelKey: 'field_name', required: true },
        { key: 'entity_type', labelKey: 'field_entity_type', type: 'select', options: entityOpts },
        { key: 'template_id', labelKey: 'field_template_id', type: 'select', options: [] },
        { key: 'status', labelKey: 'field_status', type: 'select', options: statusOpts },
        { key: 'priority', labelKey: 'field_priority', type: 'number' },
        { key: 'description', labelKey: 'field_description', type: 'textarea', fullWidth: true },
      ],
    },
    {
      id: 'steps',
      titleKey: 'section_steps',
      fields: [
        { key: 'chain_json', labelKey: 'field_chain_json', type: 'chain_steps', fullWidth: true },
        { key: 'notes', labelKey: 'field_notes', type: 'textarea', fullWidth: true },
      ],
    },
  ]), [entityOpts, statusOpts]);

  const resolveFieldOptions = useCallback((field, formData) => {
    if (field.key !== 'template_id') return null;
    return templates
      .filter((t) => !formData.entity_type || !t.entity_type || t.entity_type === formData.entity_type)
      .map((t) => ({
        value: String(t.id),
        label: t.name ? `${t.name} (${t.template_code || t.id})` : String(t.template_code || t.id),
      }));
  }, [templates]);

  const mapFormToPayload = useCallback((f) => ({
    workflow_code: f.workflow_code,
    name: (f.name || '').trim(),
    description: f.description || null,
    entity_type: f.entity_type || 'purchase_order',
    status: f.status || 'brouillon',
    priority: f.priority !== '' ? parseInt(f.priority, 10) : 0,
    chain_json: f.chain_json || '[]',
    notes: f.notes || null,
  }), []);

  const mapRecordToForm = useCallback((r) => mapRecordJsonFields(r, EMPTY), []);

  const applyTemplateToForm = useCallback((formData, templateId) => {
    if (!templateId) return formData;
    const hit = templates.find((t) => String(t.id) === String(templateId));
    if (!hit) return formData;
    return {
      ...formData,
      template_id: String(templateId),
      entity_type: formData.entity_type || hit.entity_type || '',
      chain_json: chainJsonFromTemplate(hit),
    };
  }, [templates]);

  return (
    <ApprobationsCrudShell
      ns={NS}
      icon={FilePlus}
      apiPath={API}
      codeField="workflow_code"
      entityPrefixKey="approvalWorkflow"
      codePrefixFallback="WF"
      entityLabel="مسار الاعتماد"
      entityLabelPlural="مسارات الاعتماد"
      gradient="from-emerald-500 to-emerald-700"
      emptyForm={EMPTY}
      formSections={formSections}
      mapRecordToForm={mapRecordToForm}
      mapFormToPayload={mapFormToPayload}
      resolveFieldOptions={resolveFieldOptions}
      onFormFieldChange={(key, value, prev) => {
        if (key === 'template_id' && value) return applyTemplateToForm(prev, value);
        if (key === 'entity_type' && prev.template_id) {
          const tpl = templates.find((t) => String(t.id) === String(prev.template_id));
          if (tpl && tpl.entity_type && tpl.entity_type !== value) {
            return { ...prev, entity_type: value, template_id: '', chain_json: '[]' };
          }
        }
        return { ...prev, [key]: value };
      }}
      onModalOpen={loadTemplates}
      computeStats={(records) => defaultStats(records, {
        totalIcon: FilePlus,
        fields: [
          { titleKey: 'stat_draft', field: 'status', value: 'brouillon', icon: FileText, color: 'from-amber-500 to-amber-600' },
          { titleKey: 'stat_active', field: 'status', value: 'actif', icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { titleKey: 'stat_entities', field: 'entity_type', value: 'purchase_order', icon: Layers, color: 'from-blue-500 to-blue-600' },
          { titleKey: 'stat_with_chain', match: hasMeaningfulChainJson, icon: GitCompare, color: 'from-purple-500 to-purple-600' },
        ],
      })}
      columns={[
        { key: 'workflow_code', labelKey: 'col_code' },
        { key: 'name', labelKey: 'col_name' },
        { key: 'entity_type', labelKey: 'col_entity', render: (r, _p, _t, pl) => entityLabel(r.entity_type, pl) },
        { key: 'status', labelKey: 'col_status', render: (r, _p, _t, pl) => statusBadge(r.status, pl) },
        { key: 'created_at', labelKey: 'col_created' },
      ]}
    />
  );
}
